/**
 * @fileoverview Unified Cart System Entry Point
 * Centralized cart management for the entire website
 * 
 * 📡 EVENT SUMMARY:
 * 
 * SYSTEM EVENTS:
 * - system:initialized → Emitted when cart system is fully initialized
 * - system:destroyed   → Emitted when cart system is destroyed
 * - component:added    → Emitted when a component is added
 * - component:removed  → Emitted when a component is removed
 * 
 * DELEGATED EVENTS:
 * All cart events from CartManager are re-emitted:
 * - cart:loaded, cart:updated, cart:added, cart:removed, cart:cleared
 * - operation:started, operation:completed, operation:failed
 * 
 * EVENT DATA STRUCTURE:
 * - system:initialized: { components: string[], config: CartConfig }
 * - system:destroyed: { timestamp: number }
 * - component:added: { name: string, component: any }
 * - component:removed: { name: string }
 */

import { CartManager } from './core/CartManager.js';
import { CartDrawer } from './components/CartDrawer.js';
import { QuantityManager } from './core/QuantityManager.js';
import { UIUpdater } from './ui/index.js';
import { Debug } from './utils/index.js';
import { performanceDebugger } from './utils/PerformanceDebugger.js';

export function initializeCart(config = {}) {
    // Initialize managers
    const cartManager = new CartManager(config);
    const quantityManager = new QuantityManager(cartManager);
    
    // Initialize UI components with managers (only if enabled)
    let cartDrawer = null;
    let uiUpdater = null;
    
    if (config.enableCartDrawer !== false) { // Default to true if not specified
        cartDrawer = new CartDrawer(cartManager, config);
        uiUpdater = new UIUpdater(cartManager, config);
    } else {
        console.log('[Cart] Cart drawer disabled by configuration');
    }
    
    // Add event listeners for quantity controls (only if cart drawer is enabled)
    if (cartDrawer) {
        document.addEventListener('click', (event) => {
            const quantityButton = event.target.closest('.quantity-button');
            if (quantityButton) {
                cartDrawer.handleItemAction(event);
            }
        });
    }

    // Initialize cart state
    cartManager.loadInitialCart().catch(error => {
        Debug.log('error', '[Cart] Failed to load initial cart state', error);
    });

    return {
        cartManager,
        quantityManager,
        cartDrawer,
        uiUpdater
    };
} 